/**
 * Pure silent-refresh helpers (no Vite env). Used by the web Dashboard
 * and covered by Jest so we can simulate expired trafficLiteToken safely.
 */

function decodeJwtPayload(token) {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

/**
 * @param {string|null} token
 * @param {number} [bufferMinutes=5]
 */
export function checkTokenExpiry(token, bufferMinutes = 5) {
  if (!token) {
    return { isExpired: true, isExpiringSoon: true, expiryDate: null, timeUntilExpiry: null };
  }
  const decoded = decodeJwtPayload(token);
  if (!decoded || !decoded.exp) {
    return { isExpired: true, isExpiringSoon: true, expiryDate: null, timeUntilExpiry: null };
  }
  const expTimestamp = decoded.exp * 1000;
  const now = Date.now();
  const bufferMs = bufferMinutes * 60 * 1000;
  return {
    isExpired: now >= expTimestamp,
    isExpiringSoon: now >= (expTimestamp - bufferMs),
    expiryDate: new Date(expTimestamp),
    timeUntilExpiry: expTimestamp - now
  };
}

/**
 * @param {{
 *   force?: boolean,
 *   token?: string|null,
 *   bufferMinutes?: number
 * }} [options]
 * @param {{
 *   fetchFn: typeof fetch,
 *   refreshUrl: string,
 *   readToken: () => string|null,
 *   writeToken: (previous: string, next: string) => void
 * }} deps
 */
export async function silentRefreshAccessToken(options, deps) {
  const token = options?.token ?? deps.readToken();
  if (!token) {
    return { ok: false, token: null, refreshed: false, reason: 'no_token' };
  }

  const bufferMinutes = options?.bufferMinutes ?? 5;
  const expiry = checkTokenExpiry(token, bufferMinutes);
  if (!options?.force && !expiry.isExpired && !expiry.isExpiringSoon) {
    return { ok: true, token, refreshed: false };
  }

  try {
    const response = await deps.fetchFn(deps.refreshUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let reason = `refresh_failed_${response.status}`;
      try {
        const body = await response.json();
        if (body?.error) reason = String(body.error);
      } catch (_) {
        // ignore
      }
      return { ok: false, token: null, refreshed: false, reason };
    }

    const data = await response.json().catch(() => ({}));
    const nextToken = typeof data?.token === 'string' ? data.token : null;
    if (!nextToken) {
      return { ok: false, token: null, refreshed: false, reason: 'no_token_in_response' };
    }

    deps.writeToken(token, nextToken);
    return { ok: true, token: nextToken, refreshed: true };
  } catch (_) {
    return { ok: false, token: null, refreshed: false, reason: 'network_error' };
  }
}

/**
 * @param {string} input
 * @param {RequestInit} [init]
 * @param {{
 *   fetchFn: typeof fetch,
 *   refreshUrl: string,
 *   readToken: () => string|null,
 *   writeToken: (previous: string, next: string) => void,
 *   token?: string|null
 * }} deps
 */
export async function fetchWithAuthRetry(input, init, deps) {
  const initialToken = deps.token ?? deps.readToken();
  if (!initialToken) {
    return new Response(JSON.stringify({ error: 'Not authenticated' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  const ensured = await silentRefreshAccessToken({ token: initialToken }, deps);
  const tokenForRequest = ensured.ok && ensured.token ? ensured.token : initialToken;

  const buildConfig = (bearer) => {
    const headers = new Headers(init?.headers || {});
    headers.set('Authorization', `Bearer ${bearer}`);
    if (!headers.has('Content-Type') && init?.body) {
      headers.set('Content-Type', 'application/json');
    }
    return { ...init, headers };
  };

  const first = await deps.fetchFn(input, buildConfig(tokenForRequest));
  if (first.status !== 401) {
    return first;
  }

  const refreshed = await silentRefreshAccessToken({ force: true, token: tokenForRequest }, deps);
  if (!refreshed.ok || !refreshed.token) {
    return first;
  }

  return deps.fetchFn(input, buildConfig(refreshed.token));
}
